import { ChevronIcon, CrossIcon, FileIcon, SearchIcon } from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { Spinner } from 'common/spinner';
import {
  DROPBOX_ROOT,
  DropboxAuthCancelled,
  DropboxFile,
  listFolder,
  searchDropbox,
  signInToDropbox,
  signOutFromDropbox,
} from 'helpers/dropbox';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from '../vault-import.module.scss';

const formatBytes = (bytes?: number) => {
  if (bytes == null || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i += 1;
  }
  return `${val.toFixed(val >= 10 ? 0 : 1)} ${units[i]}`;
};

interface Props {
  /** Optional banner shown above the list during browse. */
  instruction?: string;
  /** Folder selected -> resolve with folder. Picker stays mounted; parent unmounts. */
  onFolderSelected: (folder: DropboxFile) => void;
  /** User cancels (pre-auth) or hits back from the Dropbox root. */
  onClose: () => void;
  /** Surface a non-cancel error to the host. */
  onError?: (message: string) => void;
}

export const DropboxPicker = ({
  instruction,
  onFolderSelected,
  onClose,
  onError,
}: Props) => {
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const [crumbs, setCrumbs] = useState<DropboxFile[]>([DROPBOX_ROOT]);
  const [files, setFiles] = useState<DropboxFile[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DropboxFile[]>([]);
  const [searchCursor, setSearchCursor] = useState<string | undefined>();
  const [isSearching, setIsSearching] = useState(false);

  const searchTokenRef = useRef(0);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const currentFolder = crumbs[crumbs.length - 1];

  // ----- Auth -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await signInToDropbox();
        if (!cancelled) setAuthReady(true);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DropboxAuthCancelled) {
          onClose();
          return;
        }
        const message =
          err instanceof Error ? err.message : 'Failed to connect';
        setAuthError(message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onClose]);

  // ----- Folder loading -----
  const loadFolder = useCallback(
    async (path: string) => {
      setIsLoading(true);
      try {
        const page = await listFolder(path);
        setFiles(page.files);
        setNextCursor(page.cursor);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load';
        onError?.(message);
      } finally {
        setIsLoading(false);
      }
    },
    [onError],
  );

  useEffect(() => {
    if (!authReady || searchActive) return;
    loadFolder(currentFolder.pathLower);
  }, [authReady, searchActive, currentFolder.pathLower, loadFolder]);

  const loadMore = useCallback(async () => {
    if (searchActive || !nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const page = await listFolder(currentFolder.pathLower, nextCursor);
      setFiles((prev) => [...prev, ...page.files]);
      setNextCursor(page.cursor);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load';
      onError?.(message);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    searchActive,
    nextCursor,
    isLoadingMore,
    currentFolder.pathLower,
    onError,
  ]);

  const loadMoreSearch = useCallback(async () => {
    if (!searchCursor || isSearching || !searchQuery.trim()) return;
    setIsSearching(true);
    const myToken = searchTokenRef.current;
    try {
      const page = await searchDropbox(searchQuery.trim(), searchCursor);
      if (myToken !== searchTokenRef.current) return;
      setSearchResults((prev) => [...prev, ...page.files]);
      setSearchCursor(page.cursor);
    } catch (err) {
      if (myToken !== searchTokenRef.current) return;
      const message = err instanceof Error ? err.message : 'Search failed';
      onError?.(message);
    } finally {
      if (myToken === searchTokenRef.current) setIsSearching(false);
    }
  }, [searchCursor, isSearching, searchQuery, onError]);

  // Scroll-to-end pagination (window scroll)
  useEffect(() => {
    const handler = () => {
      const { scrollY } = window;
      const viewport = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollY + viewport >= docHeight - 120) {
        if (searchActive) loadMoreSearch();
        else loadMore();
      }
    };
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, [searchActive, loadMore, loadMoreSearch]);

  // ----- Search -----
  const runSearch = useCallback(
    async (query: string) => {
      const myToken = searchTokenRef.current + 1;
      searchTokenRef.current = myToken;
      const trimmed = query.trim();
      if (!trimmed) {
        setSearchResults([]);
        setSearchCursor(undefined);
        setIsSearching(false);
        return;
      }
      setIsSearching(true);
      try {
        const page = await searchDropbox(trimmed);
        if (myToken !== searchTokenRef.current) return;
        setSearchResults(page.files);
        setSearchCursor(page.cursor);
      } catch (err) {
        if (myToken !== searchTokenRef.current) return;
        const message = err instanceof Error ? err.message : 'Search failed';
        onError?.(message);
      } finally {
        if (myToken === searchTokenRef.current) setIsSearching(false);
      }
    },
    [onError],
  );

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => runSearch(value), 300);
  };

  const closeSearch = useCallback(() => {
    searchTokenRef.current += 1; // drop any in-flight search
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchActive(false);
    setSearchQuery('');
    setSearchResults([]);
    setSearchCursor(undefined);
    setIsSearching(false);
  }, []);

  const openSearch = () => {
    setSearchActive(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  // ----- Navigation -----
  const handleBack = useCallback(() => {
    if (searchActive) {
      closeSearch();
      return;
    }
    if (crumbs.length > 1) {
      setCrumbs((prev) => prev.slice(0, -1));
      return;
    }
    onClose();
  }, [searchActive, closeSearch, crumbs.length, onClose]);

  const goToCrumb = (index: number) => {
    setCrumbs((prev) => prev.slice(0, index + 1));
  };

  const enterFolder = (folder: DropboxFile) => {
    if (searchActive) closeSearch();
    setCrumbs((prev) => [...prev, folder]);
  };

  const handleSwitchAccount = async () => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'You will be signed out of Dropbox and asked to sign in again.',
    );
    if (!confirmed) return;
    await signOutFromDropbox();
    setAuthReady(false);
    setCrumbs([DROPBOX_ROOT]);
    setFiles([]);
    setNextCursor(undefined);
    closeSearch();
    try {
      await signInToDropbox({ forceReapprove: true });
      setAuthReady(true);
    } catch (err) {
      if (err instanceof DropboxAuthCancelled) {
        onClose();
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to connect';
      setAuthError(message);
    }
  };

  const visibleFiles = useMemo(
    () => (searchActive ? searchResults : files),
    [searchActive, searchResults, files],
  );

  // ----- Auth phase render -----
  if (!authReady) {
    if (authError) {
      return (
        <>
          <div className={styles.headerBar}>
            <button
              type="button"
              aria-label="Back"
              className={styles.backBtn}
              onClick={onClose}
            >
              <ChevronIcon className={styles.backIcon} />
            </button>
            <h1 className={styles.headerTitle}>Dropbox</h1>
            <span className={styles.headerSpacer} aria-hidden />
          </div>
          <div className={styles.center}>
            <p className={styles.errorText}>{authError}</p>
            <Button
              buttonStyle={ButtonStyle.Primary}
              buttonSize={ButtonSize.Large}
              onClick={onClose}
            >
              OK
            </Button>
          </div>
        </>
      );
    }
    return (
      <div className={styles.center}>
        <Spinner />
        <p className={styles.muted}>Connecting to Dropbox&hellip;</p>
      </div>
    );
  }

  // ----- Browse phase render -----
  return (
    <>
      <div className={styles.headerBar}>
        <button
          type="button"
          aria-label="Back"
          className={styles.backBtn}
          onClick={handleBack}
        >
          <ChevronIcon className={styles.backIcon} />
        </button>
        {searchActive ? (
          <input
            ref={searchInputRef}
            className={styles.searchInput}
            placeholder="Search Dropbox"
            value={searchQuery}
            onChange={onSearchChange}
          />
        ) : (
          <h1 className={styles.headerTitle}>Dropbox</h1>
        )}
        {searchActive ? (
          <button
            type="button"
            aria-label="Clear"
            className={styles.headerIconBtn}
            onClick={closeSearch}
          >
            <CrossIcon />
          </button>
        ) : (
          <>
            <button
              type="button"
              aria-label="Search"
              className={styles.headerIconBtn}
              onClick={openSearch}
            >
              <SearchIcon />
            </button>
            <button
              type="button"
              className={styles.headerPillBtn}
              onClick={handleSwitchAccount}
            >
              Switch
            </button>
          </>
        )}
      </div>

      <div className={styles.body}>
        {instruction && !searchActive && (
          <div className={styles.banner}>{instruction}</div>
        )}

        {!searchActive && (
          <div className={styles.crumbs}>
            {crumbs.map((c, i) =>
              i === crumbs.length - 1 ? (
                <span key={c.id || 'root'} className={styles.crumbCurrent}>
                  {c.name}
                </span>
              ) : (
                <span
                  key={c.id || 'root'}
                  style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  <button
                    type="button"
                    className={styles.crumbBtn}
                    onClick={() => goToCrumb(i)}
                  >
                    {c.name}
                  </button>
                  <span className={styles.crumbSep}>/</span>
                </span>
              ),
            )}
          </div>
        )}

        <div ref={listRef} className={styles.list}>
          {(() => {
            const loadingState = searchActive
              ? isSearching && visibleFiles.length === 0
              : isLoading;

            if (loadingState) {
              return (
                <div className={styles.center}>
                  <Spinner />
                </div>
              );
            }

            if (visibleFiles.length === 0) {
              let emptyText = 'This folder is empty.';
              if (searchActive) {
                emptyText = searchQuery.trim()
                  ? 'No matching files'
                  : 'Type to search your Dropbox';
              }
              return (
                <div className={styles.center}>
                  <p className={styles.muted}>{emptyText}</p>
                </div>
              );
            }

            return visibleFiles.map((f) => {
              const isSelectable = f.isFolder; // folder mode
              return (
                <button
                  type="button"
                  key={f.id || f.pathLower}
                  className={`${styles.row} ${
                    !isSelectable && !f.isFolder ? styles.rowDisabled : ''
                  }`}
                  disabled={!isSelectable}
                  onClick={() => f.isFolder && enterFolder(f)}
                >
                  <span
                    className={`${styles.rowIcon} ${
                      f.isFolder ? styles.rowIconFolder : ''
                    }`}
                  >
                    <FileIcon />
                  </span>
                  <span className={styles.rowName}>{f.name}</span>
                  {!f.isFolder && !f.isImportable && (
                    <span className={styles.unsupportedTag}>Unsupported</span>
                  )}
                  {!f.isFolder && f.size != null && (
                    <span className={styles.rowMeta}>
                      {formatBytes(f.size)}
                    </span>
                  )}
                  {f.isFolder && (
                    <span className={styles.rowChevron} aria-hidden>
                      <ChevronIcon />
                    </span>
                  )}
                </button>
              );
            });
          })()}
          {(searchActive ? isSearching : isLoadingMore) &&
            visibleFiles.length > 0 && (
              <div className={styles.center} style={{ padding: 12 }}>
                <Spinner />
              </div>
            )}
        </div>
      </div>

      {!searchActive && (
        <div className={styles.bottomBar}>
          <Button
            buttonStyle={ButtonStyle.Primary}
            buttonSize={ButtonSize.XLarge}
            onClick={() => onFolderSelected(currentFolder)}
          >
            {crumbs.length < 2
              ? 'Import all folders as albums'
              : 'Import this folder as albums'}
          </Button>
        </div>
      )}
    </>
  );
};
